import React, { useEffect, useState } from "react";
import classes from "./EditUserModal.module.css";
import API from "../../../utils/api";
import RenderToast from "../../../components/RenderToast";

const permissionGroups = [
  {
    title: "Products",
    sections: [
      {
        label: "1. Add Product Page",
        key: "product_add",
      },
      {
        label: "2. View Product Page",
        key: "product_view",
        subItems: [
          { label: "1. Add Product Button", key: "product_view_add_button" },
          { label: "2. Import Product Button", key: "product_view_import_button" },
          { label: "3. Export Csv Button", key: "product_view_export_button" },
          {
            label: "4. Action Column",
            subItems: [
              { label: "Edit", key: "product_view_edit_action" },
              { label: "View", key: "product_view_view_action" },
              { label: "Delete", key: "product_view_delete_action" },
            ],
          },
        ],
      },
      {
        label: "3. Import Product Page",
        key: "product_import",
      },
    ],
  },
  {
    title: "Category",
    sections: [
      {
        label: "1. Add Category Page",
        key: "category_add",
      },
      {
        label: "2. View Category Page",
        key: "category_view",
        subItems: [
          { label: "1. Add Category Button", key: "category_view_add_button" },
          { label: "2. Delete Selected Button", key: "category_view_delete_selected_button" },
          {
            label: "3. Action Column",
            subItems: [
              { label: "Edit", key: "category_view_edit_action" },
              { label: "View", key: "category_view_view_action" },
              { label: "Delete", key: "category_view_delete_action" },
            ],
          },
        ],
      },
    ],
  },
  {
    title: "Brand",
    sections: [
      {
        label: "1. Add Brand Page",
        key: "brand_add",
      },
      {
        label: "2. View Brand Page",
        key: "brand_view",
        subItems: [
          { label: "1. Add Brand Button", key: "brand_view_add_button" },
          { label: "2. Delete Selected Button", key: "brand_view_delete_selected_button" },
          {
            label: "3. Action Column",
            subItems: [
              { label: "Edit", key: "brand_view_edit_action" },
              { label: "View", key: "brand_view_view_action" },
              { label: "Delete", key: "brand_view_delete_action" },
            ],
          },
        ],
      },
    ],
  },
  {
    title: "Contact",
    sections: [
      { label: "1. Contact Messages Page", key: "contact_access" },
      { label: "2. Refresh Button", key: "contact_refresh_button" },
      { label: "3. Export Csv Button", key: "contact_export_button" },
      { label: "4. Delete Selected Button", key: "contact_delete_selected_button" },
      {
        label: "5. Action Column",
        subItems: [
          { label: "View", key: "contact_view_action" },
          { label: "Delete", key: "contact_delete_action" },
        ],
      },
    ],
  },
  {
    title: "Users",
    sections: [
      { label: "1. Users Page", key: "user_manage" },
      {
        label: "2. Action Column",
        subItems: [
          { label: "Edit", key: "user_edit_action" },
          { label: "View", key: "user_view_action" },
          { label: "Delete", key: "user_delete_action" },
        ],
      },
    ],
  },
  {
    title: "Dashboard",
    sections: [
      { label: "0. Show Dashboard Charts", key: "dashboard_charts" },
      { label: "0. Show Quick Actions Section", key: "dashboard_quick_action" },
      {
        label: "1. Quick Actions",
        subItems: [
          { label: "Add Product", key: "product_add" },
          { label: "Add Category", key: "category_add" },
          { label: "Add Brand", key: "brand_add" },
          { label: "Manage Users", key: "user_manage" },
          { label: "Contact Inbox", key: "contact_access" },
        ],
      },
      {
        label: "2. Summary Cards",
        subItems: [
          { label: "Products", key: "dashboard_card_products" },
          { label: "Categories", key: "dashboard_card_categories" },
          { label: "Brands", key: "dashboard_card_brands" },
        ],
      },
      { label: "3. Hide View All Links", key: "dashboard_hide_view_all" },
      { label: "4. Remove Summary Cards Section", key: "dashboard_remove_cards" },
    ],
  },
];

const roles = [
  { value: "admin", label: "Administrator" },
  { value: "user", label: "User" },
];

export default function EditUserModal({ user, onClose, onUpdated }) {
  const [form, setForm] = useState({ role: "user", permissions: {} });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    const perms = {};
    // Collect all unique permission keys from the nested structure
    const collectKeys = (sections) => {
      sections.forEach((section) => {
        if (section.key) {
          perms[section.key] = Number(user[section.key]) === 1;
        }
        if (section.subItems) {
          section.subItems.forEach((item) => {
            if (item.key) {
              perms[item.key] = Number(user[item.key]) === 1;
            }
            if (item.subItems) {
              item.subItems.forEach((subItem) => {
                if (subItem.key) {
                  perms[subItem.key] = Number(user[subItem.key]) === 1;
                }
              });
            }
          });
        }
      });
    };
    permissionGroups.forEach((group) => collectKeys(group.sections));
    setForm({ role: user.role || "user", permissions: perms });
  }, [user]);

  if (!user) return null;

  const togglePermission = (key) => {
    setForm((prev) => ({
      ...prev,
      permissions: { ...prev.permissions, [key]: !prev.permissions[key] },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem("authToken") || "";
      const params = new URLSearchParams();
      params.append("action", "update");
      params.append("id", user.id);
      params.append("role", form.role);
      params.append("permissions", JSON.stringify(form.permissions));

      const response = await fetch(`${API.base}/users.php`, {
        method: "POST",
        headers: {
          Authorization: token,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: params,
      });

      const data = await response.json();
      if (!response.ok || !data.success)
        throw new Error(data.error || "Failed to update user");

      RenderToast({ type: "success", message: "User updated successfully" });
      const updatedUser = data.user;

      if (updatedUser) {
        try {
          const storedRaw = localStorage.getItem("user");
          if (storedRaw) {
            const storedUser = JSON.parse(storedRaw);
            if (storedUser?.id && Number(storedUser.id) === Number(updatedUser.id)) {
              localStorage.setItem("user", JSON.stringify(updatedUser));
              window.dispatchEvent(new Event("userUpdated"));
            }
          }
        } catch {
          // ignore localStorage parse errors
        }
      }

      onClose();
      onUpdated?.(updatedUser);
    } catch (err) {
      RenderToast({ type: "error", message: err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={classes.backdrop} onClick={onClose}>
      <div className={classes.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <header className={classes.header}>
          <h3>Edit Access · {user.name || user.email}</h3>
          <button className={classes.closeBtn} onClick={onClose}>
            ×
          </button>
        </header>

        {/* Scrollable Content */}
        <div className={classes.contentWrapper}>
          <form className={classes.form} onSubmit={handleSubmit}>
            {/* Role */}
            <div className={classes.roleCard}>
              <label>
                <span>Role</span>
                <select
                  value={form.role}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, role: e.target.value }))
                  }
                >
                  {roles.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {/* Permissions */}
            <div className={classes.permissionsList}>
              {permissionGroups.map((group) => (
                <div key={group.title} className={classes.permissionGroup}>
                  <h4 className={classes.groupTitle}>{group.title}</h4>
                  <div className={classes.sectionsList}>
                    {group.sections.map((section, sectionIdx) => (
                      <div key={sectionIdx} className={classes.section}>
                        {section.key ? (
                          <label className={classes.checkboxLabel}>
                            <input
                              type="checkbox"
                              checked={!!form.permissions[section.key]}
                              onChange={() => togglePermission(section.key)}
                            />
                            <span className={classes.checkboxCustom}></span>
                            <span className={classes.labelText}>{section.label}</span>
                          </label>
                        ) : (
                          <div className={classes.sectionLabel}>{section.label}</div>
                        )}
                        {section.subItems && (
                          <div className={classes.subItems}>
                            {section.subItems.map((item, itemIdx) => (
                              <div key={itemIdx} className={classes.subItem}>
                                {item.key ? (
                                  <label className={classes.checkboxLabel}>
                                    <input
                                      type="checkbox"
                                      checked={!!form.permissions[item.key]}
                                      onChange={() => togglePermission(item.key)}
                                    />
                                    <span className={classes.checkboxCustom}></span>
                                    <span className={classes.labelText}>{item.label}</span>
                                  </label>
                                ) : (
                                  <div className={classes.subSectionLabel}>{item.label}</div>
                                )}
                                {item.subItems && (
                                  <div className={classes.nestedSubItems}>
                                    {item.subItems.map((subItem, subIdx) => (
                                      <label
                                        key={subIdx}
                                        className={classes.checkboxLabel}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={!!form.permissions[subItem.key]}
                                          onChange={() =>
                                            togglePermission(subItem.key)
                                          }
                                        />
                                        <span className={classes.checkboxCustom}></span>
                                        <span className={classes.labelText}>
                                          {subItem.label}
                                        </span>
                                      </label>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className={classes.actions}>
              <button
                type="button"
                className={classes.secondaryBtn}
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={classes.primaryBtn}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
